/**
 * PulsePost — Post-Pulse completion view (BHPD-5182).
 *
 * Renders after the user submits their final Pulse answer. Fetches the latest
 * score via `latestPulseScore` query (score is computed server-side inside
 * `savePulseAnswers` but not returned in its response).
 *
 * Displays: trophy illustration, score (large format), congratulatory message,
 * confetti effect, and two CTAs. Progress bar continues from PulseQuestions at
 * 100% (19 steps).
 *
 * TODO: When `pulse-error-state` component is ready, add error handling for
 * failed score fetches. Currently the page only renders loading or success.
 * See ticket AC "Score computation fails surfaces error envelope" for the
 * expected behavior.
 */
import React from "react";
import { useHistory } from "react-router-dom";
import { useQuery } from "@apollo/react-hooks";

import { ReactComponent as TrophyImage } from "assets/winners.svg";
import { Button, Confetti, Loading, ProgressBar } from "components/simple";
import { GetLatestPulseScore } from "graphql/pulse/pulse.gql";

import {
  ButtonWrapper,
  ContentWrapper,
  Explanation,
  Heading,
  PageContainer,
  Score
} from "./styled";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PulseScoreData {
  latestPulseScore: {
    pulseIndex: number;
    factor1: number;
    factor2: number;
    factor3: number;
    factor4: number;
    calculatedAt: string;
    takingMode: string;
  } | null;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PULSE_QUESTION_COUNT = 19;
const EXPLANATION_TEXT = "Congrats on completing your Pulse!";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const PulsePost: React.FC = () => {
  const history = useHistory();

  const { data, loading } = useQuery<PulseScoreData>(GetLatestPulseScore, {
    fetchPolicy: "network-only"
  });

  const score = data?.latestPulseScore?.pulseIndex;

  if (loading) {
    return (
      <>
        <ProgressBar
          activeStep={PULSE_QUESTION_COUNT}
          steps={PULSE_QUESTION_COUNT}
          variant="steps"
        />
        <PageContainer data-testid="pulse-loading">
          <Loading />
        </PageContainer>
      </>
    );
  }

  return (
    <>
      <ProgressBar
        activeStep={PULSE_QUESTION_COUNT}
        steps={PULSE_QUESTION_COUNT}
        variant="steps"
      />

      <ErrorBoundary>
        <Confetti />
      </ErrorBoundary>

      <PageContainer>
        <ContentWrapper>
          <TrophyImage
            role="img"
            aria-label="trophy"
            width={392}
            height={222}
          />
          <Heading>Your Pulse Score</Heading>
          <Score>{score != null ? Math.round(score).toString() : ""}</Score>
          <Explanation data-testid="pulse-explanation">
            {EXPLANATION_TEXT}
          </Explanation>
        </ContentWrapper>

        <ButtonWrapper>
          <Button inverted onClick={() => history.push("/pulse")}>
            View Details
          </Button>
          <Button onClick={() => history.push("/dashboard")}>
            Return to Dashboard
          </Button>
        </ButtonWrapper>
      </PageContainer>
    </>
  );
};

// ---------------------------------------------------------------------------
// ErrorBoundary — allows confetti to fail silently without crashing the page
// ---------------------------------------------------------------------------

interface ErrorBoundaryState {
  hasError: boolean;
}

class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

export default PulsePost;
